<script setup lang="ts">
import { getMetadataText } from '@@/bits/attachments'
import { dir } from '@@/bits/current_dir'
import { __ } from '@@/bits/intl'
import { postColorTailwindClass } from '@@/bits/post_color'
import { getPostPropertyFormattedValue } from '@@/bits/post_properties'
import { SURFACE_POST_COMPRESSED_HEIGHT } from '@@/bits/post_size'
import { isPostPinned } from '@@/bits/post_state'
import { hasPollAttachment as hasPollAttachmentInPost } from '@@/bits/surface_polls'
import { isUrl } from '@@/bits/url'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceAttachmentsStore } from '@@/pinia/surface_attachments'
import { useSurfacePostPropertiesStore } from '@@/pinia/surface_post_properties'
import type { Post, WallCustomPostProperty } from '@@/types'
import SurfaceAttachmentPreview from '@@/vuecomponents/SurfaceAttachmentPreview.vue'
import SurfacePostAuthor from '@@/vuecomponents/SurfacePostAuthor.vue'
import SurfacePostBody from '@@/vuecomponents/SurfacePostBody.vue'
import SurfacePostProperties from '@@/vuecomponents/SurfacePostProperties.vue'
import { asyncComputed } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

const THUMBNAIL_SIZE = 56

const props = withDefaults(
  defineProps<{
    post: Post
    isTimelinePreview?: boolean
    roundedBorders?: boolean
  }>(),
  {
    isTimelinePreview: false,
    roundedBorders: true,
  },
)

const attachmentStore = useSurfaceAttachmentsStore()
const { colorScheme, xAuthor, format, isTimelineV1 } = storeToRefs(useSurfaceStore())
const { wallCustomPostProperties } = storeToRefs(useSurfacePostPropertiesStore())

const isStream = computed(() => format.value === 'stream')

const attachmentUrl = computed<string | null>(() => {
  return props.post.attachment ?? props.post.wish_content?.attachment_props?.url ?? null
})

const hasPollAttachment = computed(() => {
  return hasPollAttachmentInPost(props.post)
})

const hasStaticAttachment = computed(() => {
  const attachment = attachmentUrl.value
  const hasAttachment = attachment != null && isUrl(attachment)
  return hasAttachment
})

const attachmentDisplayAttributes = computed(() =>
  attachmentUrl.value ? attachmentStore.getLinkDisplayAttributesForUrl(attachmentUrl.value) : null,
)

const attachmentTypeText = computed(() => {
  if (hasPollAttachment.value) return __('Poll')
  return attachmentDisplayAttributes.value ? getMetadataText(attachmentDisplayAttributes.value) : ''
})

const customThumbnailUrl = computed(() => props.post.wish_content?.attachment_props?.custom_thumbnail_url)

const xPin = computed(() => {
  return isPostPinned(props.post)
})

const postColorClass = computed(() => {
  return postColorTailwindClass({ postColor: props.post.color, isLightColorScheme: colorScheme.value === 'light' })
})

const nonEmptyCustomPostProperties = asyncComputed<WallCustomPostProperty[]>(async () => {
  const customPropertyValues = props.post.custom_properties
  if (customPropertyValues == null) return []
  return Promise.all(
    wallCustomPostProperties.value.map(async (property) => {
      if (property.id == null) return null
      const value = await getPostPropertyFormattedValue({ property, value: customPropertyValues[property.id!] })
      return value !== '' ? property : null
    }),
  ).then((results) => results.filter((p): p is WallCustomPostProperty => p !== null))
}, [])
</script>

<template>
  <div
    data-testid="surfacePostCompressed"
    :class="[
      'relative',
      'flex',
      'overflow-hidden',
      roundedBorders && {
        'rounded-xl': !isStream,
        'rounded-2xl': isStream,
      },
      'box-border',
      {
        'ps-3': !isStream,
        'ps-4': isStream,
      },
      'pe-3',
      'py-2',
      postColorClass,
      'font-sans',
    ]"
    :style="{ height: `${SURFACE_POST_COMPRESSED_HEIGHT}px` }"
  >
    <div class="flex flex-col flex-1 justify-evenly">
      <SurfacePostAuthor
        v-if="xAuthor"
        :post="post"
        :avatar-size="isTimelineV1 ? 24 : isStream ? 20 : 16"
        :dark-mode="colorScheme === 'dark'"
        :is-timeline-preview="isTimelinePreview"
        data-testid-prefix="surfacePostCompressedAuthor"
      />
      <p
        v-if="post.subject"
        :class="[
          {
            'text-17': !isStream,
            'text-heading-3': isStream,
          },
          'font-semibold',
          'break-all',
          {
            'text-light-text-100': colorScheme === 'dark',
            'text-dark-text-100': colorScheme === 'light',
          },
          xAuthor || post.body ? 'line-clamp-1' : 'line-clamp-2',
        ]"
      >
        {{ post.subject }}
      </p>
      <SurfacePostBody
        v-if="post.body"
        :class="['text-body-small-posts', 'break-all', xAuthor || post.subject ? 'line-clamp-1' : 'line-clamp-2']"
        :content="post.body"
        :dark-mode="colorScheme === 'dark'"
        :is-text-selectable="false"
      />
      <p
        v-if="!post.subject && !post.body && attachmentTypeText"
        :class="[
          {
            'text-17': !isStream,
            'text-heading-3': isStream,
          },
          'font-semibold',
          'line-clamp-1',
          'break-all',
          {
            'text-light-text-300': colorScheme === 'dark',
            'text-dark-text-300': colorScheme === 'light',
          },
        ]"
      >
        {{ attachmentTypeText }}
      </p>
      <!-- Show first custom post property value if there is no subject, body, and attachments -->
      <p v-if="!post.subject && !post.body && !attachmentTypeText && nonEmptyCustomPostProperties.length > 0">
        <SurfacePostProperties
          :dark-mode="colorScheme === 'dark'"
          :wall-custom-post-properties="[nonEmptyCustomPostProperties[0]]"
          :post="post"
          :color-scheme="colorScheme"
        />
      </p>
    </div>
    <OzIcon v-if="hasPollAttachment" :name="'poll'" class="text-park-500" :size="THUMBNAIL_SIZE" />
    <!-- The display: flex is needed to override the default display: hidden of the component for tweet previews based on the usage context -->
    <SurfaceAttachmentPreview
      v-else-if="hasStaticAttachment && attachmentUrl"
      :class="[
        'ms-2',
        'items-center',
        'justify-center',
        'h-[56px]',
        'place-self-center',
        'surface-attachment-preview-container',
        '!flex',
      ]"
      :url="attachmentUrl"
      :usage-context="'thumbnailPreview'"
      :post-cid="post.cid"
      :width="THUMBNAIL_SIZE"
      :border-radius-class="'rounded-lg'"
      :x-metadata="false"
      :fetch-priority="'low'"
      :lazy-loading="true"
      :custom-thumbnail-url="customThumbnailUrl"
      :background-color="post.color"
      :attachment-link="post.attachment_link"
    />
    <OzIcon
      v-if="xPin"
      :name="'pin_filled'"
      :dir="dir()"
      :class="[
        'absolute',
        'top-2',
        'end-2',
        {
          'text-canary-500': colorScheme === 'dark',
          'text-grape-500': colorScheme === 'light',
        },
      ]"
      :size="16"
    />
  </div>
</template>

<style scoped>
.surface-attachment-preview-container :deep(> *) {
  display: flex !important;
}
</style>
